import React, { useState } from "react";
import { useToast } from "../../../../contexts/ToastContext";

interface DiscountCellProps {
  discountType?: "percentage" | "amount";
  discountValue?: number;
  quantity: number;
  unitPrice: number;
  onDiscountChange: (updates: {
    discountType: "percentage" | "amount" | null;
    discountValue: number | null;
  }) => Promise<void> | void;
  className?: string;
}

export const DiscountCell: React.FC<DiscountCellProps> = ({
  discountType,
  discountValue,
  quantity,
  unitPrice,
  onDiscountChange,
  className = "",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editType, setEditType] = useState(discountType || "");
  const [editValue, setEditValue] = useState(discountValue || 0);
  const { addToast } = useToast();

  const validateDiscountValue = (value: number, type: string): number => {
    if (type === "percentage") {
      return Math.max(0, Math.min(100, value));
    } else if (type === "amount") {
      // Rabat kwotowy jest za sztukę - limitujemy do ceny jednostkowej
      return Math.max(0, Math.min(unitPrice, value));
    }
    return value;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (editType === "" || editValue === 0) {
        await onDiscountChange({
          discountType: null,
          discountValue: null,
        });
      } else {
        const validatedValue = validateDiscountValue(editValue, editType);
        await onDiscountChange({
          discountType: editType as "percentage" | "amount",
          discountValue: validatedValue,
        });
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save discount:", error);
      addToast({ type: "error", message: "Nie udało się zapisać rabatu" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditType(discountType || "");
    setEditValue(discountValue || 0);
    setIsEditing(false);
  };

  const formatDisplay = () => {
    if (!discountType || !discountValue) {
      return "-";
    }

    if (discountType === "percentage") {
      return `-${discountValue}%`;
    } else {
      return `-${discountValue} zł`;
    }
  };

  if (isEditing) {
    return (
      <div className={`space-y-1 ${className}`}>
        <div className="flex space-x-1">
          <select
            value={editType}
            onChange={(e) => setEditType(e.target.value)}
            className="flex-1 p-1 border border-blue-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">-</option>
            <option value="percentage">%</option>
            <option value="amount">PLN</option>
          </select>
          <input
            type="number"
            min="0"
            max={editType === "percentage" ? 100 : unitPrice}
            step={editType === "percentage" ? "1" : "0.01"}
            value={editValue || ""}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              const validated = validateDiscountValue(value, editType);
              setEditValue(validated);
            }}
            className="flex-1 p-1 border border-blue-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="0"
            disabled={!editType}
          />
        </div>
        <div className="flex space-x-1">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-1 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "…" : "✓"}
          </button>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="flex-1 px-1 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className={`cursor-pointer hover:bg-gray-100 rounded px-1 py-1 transition-colors text-center text-xs text-gray-600 ${className}`}
      title="Kliknij aby edytować rabat"
    >
      {formatDisplay()}
    </div>
  );
};
